import React, { useContext, useEffect, useState } from "react";
import { CSVLink } from "react-csv";
import { FilterMatchMode } from "primereact/api";
import { Button, Dialog, TabView } from "iuvity-ui-components";
import { Upload } from "iuvity-ui-components/dist/upload";
import { CustomInput } from "../inputs/CustomInput";
import { DialogProps } from "../dialog/CustomDialog";
import { Column } from "iuvity-ui-components/dist/column";
import { DataTable } from "iuvity-ui-components/dist/datatable";
import { TableHeader } from "../datatable/TableHeader";
import { ConsoleTranslationsImportSchema } from "../../validations/schemas";
import { AppContext } from "../../context";
import Papa from "papaparse";

interface TranslationsImporterProps {
  allowedIDs: string[];
  columns: any;
  importFn: Function;
  reloadFn: Function;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  auxModalController: React.Dispatch<React.SetStateAction<DialogProps>>;
  headers?: string[];
}

const csvHeaders = [
  "id",
  "widgets",
  "key",
  "version",
  "es",
  "en",
  "type",
  "description",
];

const RenderTable = ({ columns, data }: any) => {
  const {
    t,
    appState: { language },
  } = useContext(AppContext);

  const [selectedColumns, setSelectedColumns] = useState(columns);
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  useEffect(() => {
    if (language)
      setSelectedColumns((prev: any) =>
        prev.map((col: any) => ({
          field: col.field,
          header: t(
            `Translations.${
              col.field === "version" ? "releaseversion" : col?.field
            }Label`
          ),
        }))
      );
  }, [t, language, setSelectedColumns]);

  return (
    <DataTable
      className="mt-4"
      header={
        <TableHeader
          title={t("Translations.translationsTitle")}
          columns={columns}
          selectedColumns={selectedColumns}
          toggleColumns={setSelectedColumns}
          filters={filters}
          setFilters={setFilters}
        />
      }
      currentPageReportTemplate={t(
        "ConsoleTranslations.rowsPaginatorTemplateLabel"
      )}
      paginatorRight={t('ConsoleTranslations.itemsTextDatatableLabel')}
      emptyMessage={t("TableActions.emptyMessage")}
      rows={10}
      size="normal"
      editMode="row"
      dataKey="id"
      sortField="id"
      sortOrder={1}
      filters={filters}
      value={data}
    >
      {selectedColumns.map((col: any) => (
        <Column
          key={col.field}
          field={col.field}
          header={col.header}
          sortable={true}
          align="left"
        />
      ))}
    </DataTable>
  );
};

export const TranslationsImporter: React.FC<TranslationsImporterProps> = ({
  allowedIDs,
  columns,
  importFn,
  reloadFn,
  setVisible,
  auxModalController,
  headers = csvHeaders,
}) => {
  const {
    t,
    appState: { selectedApp },
  } = useContext(AppContext);

  const [filePath, setFilePath] = useState<string>("");
  const [successData, setSuccessData] = useState<any[]>([]);
  const [errorData, setErrorData] = useState<any[]>([]);
  const [uploadErrorMsg, setuploadErrorMsg] = useState<boolean>(false);
  const [tabIndex, setTabIndex] = useState<number>(0);

  const onCancel = () => {
    setVisible(false);
  };

  const onImport = async () => {
    const actionStatus = await importFn("IMPORT", {
      applicationid: selectedApp?.appid,
      importInfo: successData.map((label) => ({
        id: label?.id,
        key: label?.key,
        description: label?.description,
        en: label?.envalue,
        es: label?.esvalue,
      })),
    });
    if (actionStatus === "SUCCESS") {
      onCancel();
      reloadFn();
    }
  };

  const handleModalProps = () => {
    auxModalController((prev) => ({
      ...prev,
      visible: true,
      title: t(`Translations.importModalTitle`),
      content: `${t(`Translations.importModalContent`)}`,
      onConfirm: onImport,
    }));
  };

  const fieldsValidator = (label: any) => {
    const validationRules = ConsoleTranslationsImportSchema(t);
    const validations: any[] = [];

    if (!allowedIDs.includes(`${label.id}`)) {
      validations.push(t("Translations.idErrorMessage"));
    }

    ["functionality", "key", "type", "esvalue", "envalue", "description"].forEach((key: any) => {
      const minLengthValue = (validationRules as any)[key].minLength.value;
      const maxLengthValue = (validationRules as any)[key].maxLength.value;
      const patternValue = (validationRules as any)[key].pattern.value;

      if (label[key]) {
        if (label[key].length < (key === "description" ? 0 : minLengthValue)) {
          validations.push(
            `${t("Translations.errorMessageStarter")} (${t(
              `Translations.${key}Label`
            )}) ${t("Translations.minLengthErrorMessage")} (${minLengthValue})`
          );
        }
        if (label[key].length > maxLengthValue) {
          validations.push(
            `${t("Translations.errorMessageStarter")} (${t(
              `Translations.${key}Label`
            )}) ${t("Translations.maxLengthErrorMessage")} (${maxLengthValue})`
          );
        }
        if (label[key].length > 0 && !label[key].match(patternValue)) {
          validations.push(
            `${t("Translations.errorMessageStarter")} (${t(
              `Translations.${key}Label`
            )}) ${t("Translations.patternErrorMessage")}`
          );
        }
      }
    });
    return validations.join(". ");
  };

  const invoiceUploadHandler = ({ files }: any) => {
    const [file] = files;
    setFilePath(file.name);
    try {
      Papa.parse(file, {
        header: true,
        download: true,
        delimiter: ",",
        complete: (result: any) => {
          if (
            result?.meta?.fields.every((el: string) => headers.includes(el))
          ) {
            const parsedData = result?.data
              .filter((label: any) => label?.id)
              .map((label: any) => ({
                ...label,
                esvalue: label?.es,
                envalue: label?.en,
                functionality: label?.widgets || label?.functionality,
              }))
              .map((label: any) => ({
                ...label,
                validations: fieldsValidator(label),
              }));
            setSuccessData(
              parsedData.filter((label: any) => label?.validations.length === 0)
            );
            setErrorData(
              parsedData.filter((label: any) => label?.validations.length !== 0)
            );
          } else {
            setuploadErrorMsg(true);
          }
        },
      });
      return true;
    } catch (error) {
      setuploadErrorMsg(true);
    }
  };

  const invoiceRemoveHandler = () => {
    setSuccessData([]);
    setErrorData([]);
    setTabIndex(0);
    setFilePath("");
    setuploadErrorMsg(false);
  };

  const renderFooter = (tabIndex: number) => {
    return tabIndex !== 0 ? (
      <div className="flex align-items-center justify-content-center">
        <CSVLink
          className="p-button ntd mr-4"
          headers={headers.map((key) => ({
            label: key,
            key,
          }))}
          data={errorData}
          enclosingCharacter={`"`}
          filename={`${t("Translations.errorsLabel")}-${filePath}`}
        >
          {t("Translations.errorsReportBtnLabel")}
        </CSVLink>
      </div>
    ) : (
      <div className="flex align-items-center justify-content-center">
        <Button
          label={t("Translations.cancelBtnLabel")}
          onClick={onCancel}
          className="mr-4"
          variant="p-button-link"
        />
        <Button
          label={t("Translations.importBtnLabel")}
          variant="p-button"
          onClick={handleModalProps}
          disabled={Object.entries(successData).length === 0}
        />
      </div>
    );
  };

  return (
    <Dialog
      id="dialogScroll1"
      className="dialog-form"
      header={t("Translations.importTranslationsTitle")}
      visible={true}
      onHide={onCancel}
      style={{
        width: `${
          successData.length + errorData.length === 0
            ? "50"
            : errorData.length > 0
            ? "70"
            : "60"
        }rem`,
      }}
      footer={renderFooter(tabIndex)}
    >
      <>
        <div
          className={`upload-container ${
            Object.keys(successData).length > 0 ? "mb-4" : ""
          }`}
        >
          <div className="upload-input-container mr-4">
            <span
              style={{
                textAlign: "center",
              }}
            >
              {t("Translations.importFilenameLabel")}
            </span>
            <CustomInput
              id="input-file"
              className="upload-input rd-only"
              placeholder={t(`Translations.uploadFilePlaceholder`)}
              type="text"
              autoComplete="off"
              readOnly
              value={filePath}
            />
          </div>
          <Upload
            className={
              Object.keys(successData).length + Object.keys(errorData).length >
              0
                ? "disabled"
                : ""
            }
            chooseLabel={
              Object.keys(successData).length + Object.keys(errorData).length >
              0
                ? t("Translations.uploadedBtnLabel")
                : t("Translations.uploadBtnLabel")
            }
            mode="basic"
            accept=".csv"
            customUpload={true}
            onClear={invoiceRemoveHandler}
            uploadHandler={invoiceUploadHandler}
            auto={true}
            disabled={Object.keys(successData).length > 0}
          />
        </div>
        {uploadErrorMsg && (
          <span className="p-error block mt-5">
            {t("Translations.uploadFileErrorMsgLabel")}
          </span>
        )}
        {successData.length > 0 && errorData.length === 0 && (
          <>
            <span className="block mt-4">
              {t("Translations.labelsToImportDescription")}
            </span>
            {<RenderTable columns={columns} data={successData} />}
          </>
        )}
        {errorData.length > 0 && (
          <TabView
            className="upload-tabview mt-4"
            activeIndex={tabIndex}
            onTabChange={(e) => setTabIndex(e.index)}
            items={[
              {
                key: "1",
                header: t("Translations.labelsToImportTitle"),
                children: (
                  <>
                    <span className="block mt-4">
                      {t("Translations.labelsToImportDescription")}
                    </span>
                    {<RenderTable columns={columns} data={successData} />}
                  </>
                ),
                disabled: false,
              },
              {
                key: "2",
                header: t("Translations.errorLabelsTitle"),
                children: (
                  <>
                    <span className="block mt-4">
                      {t("Translations.errorLabelsDescription")}
                    </span>
                    {
                      <RenderTable
                        columns={[
                          ...columns,
                          {
                            field: "validations",
                            header: t("Translations.validationsLabel"),
                          },
                        ]}
                        data={errorData}
                      />
                    }
                  </>
                ),
                disabled: false,
              },
            ]}
          />
        )}
      </>
    </Dialog>
  );
};
